import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { s } from '../PeriodStatsPage.styles';
import { downloadAsExcel } from '../../../../utils/downloadAsExcel';
import { formatCurrency } from '../../../../func/Function';
import { useDragScroll } from '../../../../utils/useDragScroll';
import BottomModal from '../../../components/modal/BottomModal';

type RegionAuctionOverviewProps = {
  periodSummary: string;
  region: string;
  data?: RegionMetricsItem[];
  loading?: boolean;
  error?: string | null;
  usage: string;
  usageDetail?: string;
  fiMain?: string;
  fiSub?: string;
};

export type RegionMetricsItem = {
  id: string;
  region: string; // "sido"
  claimAmount: number; // 청구금액
  bidRate: number; // 낙찰률(%)
  winPriceRate: number; // 낙찰가율(%)
  collateralValueRate: number; // 담보가치율(%)
  recoveryRate: number; // 회수율(%)
  lossRate: number; // 손실률(%)
  mortgageAmount?: number; // 근저당액
  shortfallRate?: number; // 회수부족율
};

export default function RegionalNPLOverview({
  periodSummary,
  region,
  data = [],
  loading = false,
  error = null,
  usage,
  usageDetail,
  fiMain,
  fiSub,
}: RegionAuctionOverviewProps) {
  type SortKey =
    | 'claimAmount'
    | 'bidRate'
    | 'winPriceRate'
    | 'collateralValueRate'
    | 'recoveryRate'
    | 'lossRate';
  type SortDir = 'asc' | 'desc';

  const [sortKey, setSortKey] = useState<SortKey>('claimAmount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove } =
    useDragScroll();

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const rows = useMemo(() => {
    const cloned = [...(data ?? [])];
    cloned.sort((a, b) => {
      const va = (a as any)[sortKey] ?? 0;
      const vb = (b as any)[sortKey] ?? 0;
      const diff = (va as number) - (vb as number);
      return sortDir === 'asc' ? diff : -diff;
    });
    return cloned;
  }, [data, sortKey, sortDir]);

  const avg = useMemo(() => {
    const n = data?.length ?? 0;
    if (!n) {
      return {
        claimAmount: 0,
        bidRate: 0,
        winPriceRate: 0,
        collateralValueRate: 0,
        recoveryRate: 0,
        lossRate: 0,
      };
    }

    const sum = data!.reduce(
      (a, c) => ({
        claimAmount: a.claimAmount + (c.claimAmount ?? 0),
        bidRate: a.bidRate + (c.bidRate ?? 0),
        winPriceRate: a.winPriceRate + (c.winPriceRate ?? 0),
        collateralValueRate:
          a.collateralValueRate + (c.collateralValueRate ?? 0),
        recoveryRate: a.recoveryRate + (c.recoveryRate ?? 0),
        lossRate: a.lossRate + (c.lossRate ?? 0),
      }),
      {
        claimAmount: 0,
        bidRate: 0,
        winPriceRate: 0,
        collateralValueRate: 0,
        recoveryRate: 0,
        lossRate: 0,
      },
    );
    return {
      claimAmount: Math.round(sum.claimAmount / n),
      bidRate: +(sum.bidRate / n).toFixed(1),
      winPriceRate: +(sum.winPriceRate / n).toFixed(1),
      collateralValueRate: +(sum.collateralValueRate / n).toFixed(1),
      recoveryRate: +(sum.recoveryRate / n).toFixed(1),
      lossRate: +(sum.lossRate / n).toFixed(1),
    };
  }, [data]);

  const handleDownloadExcel = () => {
    const xlsxRows = rows.map((r) => ({
      지역: r.region,
      청구금액: r.claimAmount,
      '낙찰률(%)': r.bidRate,
      '낙찰가율(%)': r.winPriceRate,
      '담보가치율(%)': r.collateralValueRate,
      '회수율(%)': r.recoveryRate,
      '손실률(%)': r.lossRate,
    }));

    const avgRow = {
      지역: '평균',
      청구금액: avg.claimAmount,
      '낙찰률(%)': avg.bidRate,
      '낙찰가율(%)': avg.winPriceRate,
      '담보가치율(%)': avg.collateralValueRate,
      '회수율(%)': avg.recoveryRate,
      '손실률(%)': avg.lossRate,
    };

    downloadAsExcel([...xlsxRows, avgRow] as any[], '지역별_NPL_지표');
  };

  const [selectedRow, setSelectedRow] = useState<RegionMetricsItem | null>(
    null,
  );

  return (
    <s.Wrap>
      <s.Header>
        <s.HeaderRow>
          <s.Img $bg="#dcf7e8">
            <img src="/assets/images/icon/green_map.svg" alt="" />
          </s.Img>
          <s.ExcelButton onClick={handleDownloadExcel} aria-label="엑셀 저장">
            <img src="/assets/images/icon/download-black.svg" alt="" />
          </s.ExcelButton>
        </s.HeaderRow>
        <s.TitleBox>
          <h3>지역별 NPL 지표 현황</h3>
          <p>전국 주요 지역별 경매 대상 NPL 시장 현황 비교</p>
          <s.SelectedOptionBox>
            <s.SelectedOption>{periodSummary}</s.SelectedOption>
            <s.SelectedOption>
              {usage === '전체'
                ? `${usage} 용도`
                : `${usage} ${usageDetail ? ` ${usageDetail} 용도` : ''}`}
            </s.SelectedOption>
            <s.SelectedOption>
              {fiMain === '전체'
                ? `${fiMain} 금융기관`
                : `${fiSub ? ` ${fiSub}` : ''}`}
            </s.SelectedOption>
          </s.SelectedOptionBox>
        </s.TitleBox>
      </s.Header>

      {loading ? (
        <LoaderWrap>
          <LoaderImg
            src="/assets/images/icon/loader.gif"
            alt="지역별 경매 지표"
          />
        </LoaderWrap>
      ) : (
        <TableContainer
          ref={ref}
          onMouseDown={onMouseDown}
          onMouseLeave={onMouseLeave}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
        >
          <s.Table style={{ width: '100%' }}>
            <thead>
              <tr>
                <Th align="left">지역</Th>

                <Th align="right">
                  <SortHeader onClick={() => toggleSort('claimAmount')}>
                    <Arrow $active={sortKey === 'claimAmount'} $dir={sortDir} />
                    청구금액
                  </SortHeader>
                </Th>

                <Th align="right">
                  <SortHeader onClick={() => toggleSort('bidRate')}>
                    <Arrow $active={sortKey === 'bidRate'} $dir={sortDir} />
                    낙찰률
                  </SortHeader>
                </Th>

                <Th align="right">
                  <SortHeader onClick={() => toggleSort('winPriceRate')}>
                    <Arrow
                      $active={sortKey === 'winPriceRate'}
                      $dir={sortDir}
                    />
                    낙찰가율
                  </SortHeader>
                </Th>

                <Th align="right">
                  <SortHeader onClick={() => toggleSort('collateralValueRate')}>
                    <Arrow
                      $active={sortKey === 'collateralValueRate'}
                      $dir={sortDir}
                    />
                    담보가치율
                  </SortHeader>
                </Th>

                <Th align="right">
                  <SortHeader onClick={() => toggleSort('recoveryRate')}>
                    <Arrow
                      $active={sortKey === 'recoveryRate'}
                      $dir={sortDir}
                    />
                    회수율
                  </SortHeader>
                </Th>

                <Th align="right">
                  <SortHeader onClick={() => toggleSort('lossRate')}>
                    <Arrow $active={sortKey === 'lossRate'} $dir={sortDir} />
                    손실률
                  </SortHeader>
                </Th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => (
                <Row
                  key={row.id}
                  $selected={row.id === selectedRow?.id}
                  onClick={() => setSelectedRow(row)}
                >
                  <Td align="left">{row.region}</Td>
                  <Td align="right">{formatCurrency(row.claimAmount)}</Td>
                  <Td align="right">{row.bidRate}%</Td>
                  <Td align="right">{row.winPriceRate}%</Td>
                  <Td align="right">{row.collateralValueRate}%</Td>
                  <Td align="right">{row.recoveryRate}%</Td>
                  <Td align="right">{row.lossRate}%</Td>
                </Row>
              ))}

              <s.AvgTr>
                <s.TdAvg align="left">평균</s.TdAvg>
                <s.TdAvg align="right">
                  {formatCurrency(avg.claimAmount)}
                </s.TdAvg>
                <s.TdAvg align="right">{avg.bidRate}%</s.TdAvg>
                <s.TdAvg align="right">{avg.winPriceRate}%</s.TdAvg>
                <s.TdAvg align="right">{avg.collateralValueRate}%</s.TdAvg>
                <s.TdAvg align="right">{avg.recoveryRate}%</s.TdAvg>
                <s.TdAvg align="right">{avg.lossRate}%</s.TdAvg>
              </s.AvgTr>
            </tbody>
          </s.Table>
        </TableContainer>
      )}

      {selectedRow && (
        <BottomModal onClose={() => setSelectedRow(null)}>
          <TooltipHeader>
            <HeaderLeft>
              <BadgeLabel>{selectedRow.region}</BadgeLabel>
              <TooltipTitle>{periodSummary}</TooltipTitle>
            </HeaderLeft>
            <CloseBtn
              onClick={() => setSelectedRow(null)}
              aria-label="툴팁 닫기"
            >
              ×
            </CloseBtn>
          </TooltipHeader>
          <TooltipList>
            <TipItem>
              <span>청구금액</span>
              <strong>{formatCurrency(selectedRow.claimAmount)}</strong>
            </TipItem>
            <TipItem>
              <span>근저당액</span>
              <strong>
                {selectedRow.mortgageAmount != null
                  ? formatCurrency(selectedRow.mortgageAmount)
                  : '-'}
              </strong>
            </TipItem>
            <TipItem>
              <span>낙찰률</span>
              <strong>{`${selectedRow.bidRate.toFixed?.(1) ?? selectedRow.bidRate}%`}</strong>
            </TipItem>
            <TipItem>
              <span>낙찰가율</span>
              <strong>{`${selectedRow.winPriceRate.toFixed?.(1) ?? selectedRow.winPriceRate}%`}</strong>
            </TipItem>
            <TipItem>
              <span>담보가치율</span>
              <strong>{`${selectedRow.collateralValueRate.toFixed?.(1) ?? selectedRow.collateralValueRate}%`}</strong>
            </TipItem>
            <TipItem>
              <span>회수율</span>
              <strong>{`${selectedRow.recoveryRate.toFixed?.(1) ?? selectedRow.recoveryRate}%`}</strong>
            </TipItem>
            <TipItem>
              <span>손실률</span>
              <strong>{`${selectedRow.lossRate.toFixed?.(1) ?? selectedRow.lossRate}%`}</strong>
            </TipItem>
            <TipItem>
              <span>회수부족율</span>
              <strong>
                {selectedRow.shortfallRate != null
                  ? `${Number(selectedRow.shortfallRate).toFixed?.(1) ?? selectedRow.shortfallRate}%`
                  : '-'}
              </strong>
            </TipItem>
          </TooltipList>
        </BottomModal>
      )}
    </s.Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 40px;
`;

const Th = styled.th<{ align?: 'left' | 'center' | 'right' }>`
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: -2%;
  angle: 0 deg;
  opacity: 1;
  gap: 4px;
  padding: 12px 14px 12px 12px;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
`;

const Td = styled.td<{ align?: 'left' | 'center' | 'right' }>`
  padding: 14px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
`;

const SortHeader = styled.button`
  font-weight: 500;
  font-size: 13px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: -2%;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  cursor: pointer;
  background: transparent;
  border: 0;
  padding: 0;
`;

const Arrow = styled.img.attrs<{ $active?: boolean; $dir?: 'asc' | 'desc' }>(
  ({ $active, $dir }) => {
    if ($active) {
      return {
        src:
          $dir === 'desc'
            ? '/assets/images/arrow/blue_arrow_down.svg' // 활성 + 오름
            : '/assets/images/arrow/red_arrow_up.svg', // 활성 + 내림
        alt: '',
      };
    }
    return { src: '/assets/images/arrow/gray_arrow_down.svg', alt: '' }; // 비활성
  },
)<{ $active?: boolean; $dir?: 'asc' | 'desc' }>`
  width: 16px;
  height: 16px;
  transition:
    transform 120ms ease,
    opacity 120ms ease;
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`;

const LoaderImg = styled.img`
  width: 24px;
  height: 24px;
`;

const Row = styled.tr<{ $selected?: boolean }>`
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#E8E9ED' : 'transparent')};
  &:hover {
    background: ${({ $selected }) => ($selected ? '#E8E9ED' : '#F4F5F7')};
  }
  transition: background 120ms ease;
`;

// 툴팁 UI
const TooltipWrap = styled.div`
  position: fixed; /* 커서 기준 고정 */
  z-index: 9999;
  min-width: 240px;
  min-height: 364px;
  background: #fff;
  border-radius: 8px;
  box-shadow:
    (0px 8px 32px 0px #272c3614), (0px 2px 4px 0px #272c361f),
    (0px 4px 12px 0px #272c360d);

  padding: 20px 16px 40px;
  filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.12));
`;

const TooltipHeader = styled.div`
  display: flex;
  padding: 24px 20px 8px 20px;
  align-items: center;
  align-self: stretch;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
`;

const BadgeLabel = styled.span`
  background: #e8e9ed;
  color: #424854;
  font-weight: 700;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 6px;
`;

const TooltipTitle = styled.div`
  color: #424854;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 150% */
  letter-spacing: -0.32px;
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  outline: none;

  font-size: 16px;
  color: #787d87;
  cursor: pointer;
  line-height: 1;
`;

const TooltipList = styled.div`
  display: flex;
  padding-top: 8px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const TipItem = styled.div`
  display: flex;
  padding: 4px 24px 12px 24px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  align-self: stretch;

  span {
    font-size: 13px;
    color: #787d87;
  }
  strong {
    font-size: 14px;
    color: #424854;
  }
`;

const ToolTipBottom = styled.div`
  display: flex;
  padding: 16px 24px 24px 24px;
  align-items: center;
  gap: 8px;
`;

const ToolTipButton = styled.div`
  display: flex;
  height: 44px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
  border-radius: 8px;
  background: var(--npl-semantic-background-solid-light-01, #f4f5f7);
  color: var(--npl-semantic-label-solid-light-secondary, #424854);

  /* npl/body/med/L_15 */
  font-family: 'Spoqa Han Sans Neo';
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px; /* 146.667% */
  letter-spacing: -0.3px;
`;
const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  cursor: grab;

  &.grabbing {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
`;
